import React, { useState } from 'react';
import {
    Box,
    CardContent,
    CardHeader,
    FormControlLabel,
    MenuItem,
    Typography,
    useTheme,
} from '@mui/material';
import { DropdownButton } from '@europrocurement/flexy-components/components/molecules/DropdownButton';
import { EmptyBox, ListCardBone, Radio, SkeletonLoader } from '@europrocurement/flexy-components';

import { type DossierTaches } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import { type PrescripteurTaches } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { type UtilisateurTaches } from '@europrocurement/l2d-domain';
import CheckableTaskRow from '../CheckableTaskRow/CheckableTaskRow';

type TasksCardContentProps = {
    tasks: DossierTaches[] | PrescripteurTaches[] | UtilisateurTaches[];
    tasksLoading: boolean;
    onCheckTask: (linkedTask: DossierTaches | PrescripteurTaches | UtilisateurTaches) => void;
    onEditTask: (linkedTask: DossierTaches | PrescripteurTaches | UtilisateurTaches) => void;
    onDeleteTask: (id: number) => void;
    userIdentifier: string;
};

const TasksCardContent: React.FunctionComponent<TasksCardContentProps> = function ({
    tasks,
    tasksLoading,
    onCheckTask,
    onEditTask,
    onDeleteTask,
    userIdentifier,
}) {
    const { palette } = useTheme();
    const [activeFilter, setActiveFilter] = React.useState<string | number>('all');
    const [filterOpen, setFilterOpen] = useState(false);
    const filterOptions = React.useMemo(
        () => [
            { value: 'all', label: 'Toutes les tâches' },
            { value: 'myTasks', label: 'Uniquement mes tâches' },
            { value: 2, label: 'Uniquement les tâches de priorité élevée' },
            { value: 1, label: 'Uniquement les tâches de priorité moyenne' },
            { value: 0, label: 'Uniquement les tâches de priorité faible' },
            { value: 'unchecked', label: 'Uniquement les tâches non terminées' },
        ],
        [],
    );

    const filteredTasks = React.useMemo(
        () =>
            tasks.filter((task) => {
                if (activeFilter === 'all') {
                    return true;
                }
                if (activeFilter === 'myTasks') {
                    return task.tache?.assignTo?.userIdentifier === userIdentifier;
                }

                if (activeFilter === 'unchecked') {
                    return task.tache?.isDone === false;
                }
                return task.tache?.priority === activeFilter;
            }),
        [tasks, activeFilter, userIdentifier],
    );

    return (
        <Box sx={{ padding: '0 20px 20px' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    paddingTop: '16px',
                    paddingBottom: '16px',
                }}
            >
                <CardHeader
                    title="Tâches"
                    subtitle={
                        <Typography
                            variant="l2dsubtitles"
                            sx={{ marginLeft: '10px' }}
                        >
                            Prinicpe
                        </Typography>
                    }
                    headerSx={{ paddingTop: '0px' }}
                />
                <Box>
                    <DropdownButton
                        label={
                            filterOptions.find((opt) => opt.value === activeFilter)?.label ||
                            'Toutes les activités'
                        }
                        variant={'outlined' as const}
                        size={'small' as const}
                        color={palette.text.secondary}
                        style={{ width: '100%' }}
                        open={filterOpen}
                        onOpenChange={setFilterOpen}
                    >
                        <Box>
                            {filterOptions.map((option) => (
                                <MenuItem key={option.value}>
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                checked={activeFilter === option.value}
                                                onChange={() => {
                                                    setActiveFilter(option.value);
                                                    setFilterOpen(false);
                                                }}
                                            />
                                        }
                                        label={option.label}
                                    />
                                </MenuItem>
                            ))}
                        </Box>
                    </DropdownButton>
                </Box>
            </Box>
            <CardContent sx={{ padding: 0 }}>
                <SkeletonLoader
                    isLoading={tasksLoading}
                    skeleton={
                        <Box
                            sx={{ display: 'flex', justifyContent: 'center', padding: '20px 20px' }}
                        >
                            <ListCardBone
                                contentWidth="100%"
                                contentHeight="90px"
                            />
                        </Box>
                    }
                >
                    <Box sx={{ padding: '0 20px 20px' }}>
                        {filteredTasks && filteredTasks.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {filteredTasks.map((task) => (
                                    <CheckableTaskRow
                                        key={`tache_${task.id}`}
                                        test-id="checkable-task"
                                        task={task}
                                        handleCheckTask={onCheckTask}
                                        onEditTask={onEditTask}
                                        canDeleteTask={
                                            userIdentifier === task.tache?.createdBy?.userIdentifier
                                        }
                                        onDeleteTask={onDeleteTask}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <EmptyBox
                                text="Pas de tâches à afficher"
                                sx={{
                                    marginBottom: '-40px',
                                }}
                            />
                        )}
                    </Box>
                </SkeletonLoader>
            </CardContent>
        </Box>
    );
};

export default TasksCardContent;
